import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { ClipboardCheck, Plus, Trash2, Eye, FileText, CheckCircle, XCircle, AlertTriangle, ChevronDown, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const EVALUATION_CRITERIA = [
  { id: "pre_op_inspection", label: "Performs pre-operation inspection correctly", labelEs: "Realiza la inspeccion pre-operativa correctamente", category: "Pre-Operation" },
  { id: "controls_instrumentation", label: "Understands controls and instrumentation", labelEs: "Comprende los controles e instrumentacion", category: "Knowledge" },
  { id: "seatbelt_safety", label: "Uses seatbelt and safety devices", labelEs: "Usa cinturon de seguridad y dispositivos de seguridad", category: "Safety" },
  { id: "travel_speed", label: "Maintains proper travel speed", labelEs: "Mantiene velocidad de viaje adecuada", category: "Operation" },
  { id: "forks_load_height", label: "Keeps forks/load at proper height while traveling", labelEs: "Mantiene horquillas/carga a la altura adecuada mientras viaja", category: "Operation" },
  { id: "turning_maneuvering", label: "Demonstrates safe turning and maneuvering", labelEs: "Demuestra giros y maniobras seguras", category: "Operation" },
  { id: "distance_personnel", label: "Maintains safe distance from personnel", labelEs: "Mantiene distancia segura del personal", category: "Safety" },
  { id: "handles_loads", label: "Properly handles and secures loads", labelEs: "Maneja y asegura cargas correctamente", category: "Operation" },
  { id: "load_capacity", label: "Understands load capacity and stability", labelEs: "Comprende capacidad de carga y estabilidad", category: "Knowledge" },
  { id: "hazard_recognition", label: "Recognizes hazards (overhead, uneven ground, pedestrians)", labelEs: "Reconoce peligros (aereos, terreno irregular, peatones)", category: "Safety" },
  { id: "horn_signals", label: "Uses horn and signals appropriately", labelEs: "Usa bocina y señales apropiadamente", category: "Operation" },
  { id: "parks_safely", label: "Parks equipment safely and correctly", labelEs: "Estaciona equipo de forma segura y correcta", category: "Operation" },
  { id: "site_safety_rules", label: "Follows site-specific safety rules", labelEs: "Sigue las reglas de seguridad del sitio", category: "Safety" },
];

const OperatorEvaluation = ({ user }) => {
  const { t, i18n } = useTranslation();
  const isEs = i18n.language === 'es';

  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingEvaluation, setViewingEvaluation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    employeeName: '',
    company: user?.company || '',
    equipmentType: 'Forklift Gradall',
    evaluationDate: new Date().toISOString().split('T')[0],
    trainerEvaluator: user?.name || '',
    evaluationResult: '',
    evaluatorSignature: '',
    signatureDate: new Date().toISOString().split('T')[0]
  });

  const [criteriaResults, setCriteriaResults] = useState(
    EVALUATION_CRITERIA.map(c => ({ id: c.id, result: '', comments: '' }))
  );

  useEffect(() => {
    loadEvaluations();
  }, []);

  const loadEvaluations = async () => {
    try {
      const res = await axiosInstance.get('/inspections/evaluations');
      setEvaluations(res.data);
    } catch (err) {
      console.error('Failed to load evaluations:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      employeeName: '',
      company: user?.company || '',
      equipmentType: 'Forklift Gradall',
      evaluationDate: new Date().toISOString().split('T')[0],
      trainerEvaluator: user?.name || '',
      evaluationResult: '',
      evaluatorSignature: '',
      signatureDate: new Date().toISOString().split('T')[0]
    });
    setCriteriaResults(EVALUATION_CRITERIA.map(c => ({ id: c.id, result: '', comments: '' })));
  };

  const openNewForm = () => {
    resetForm();
    setShowFormDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.employeeName.trim()) {
      toast.error(isEs ? 'Nombre del empleado es requerido' : 'Employee name is required');
      return;
    }
    if (!formData.evaluationResult) {
      toast.error(isEs ? 'Selecciona un resultado de evaluacion' : 'Select an evaluation result');
      return;
    }
    if (!formData.evaluatorSignature.trim()) {
      toast.error(isEs ? 'Firma del evaluador es requerida' : 'Evaluator signature is required');
      return;
    }

    const unanswered = criteriaResults.filter(c => !c.result);
    if (unanswered.length > 0) {
      toast.error(isEs ? `${unanswered.length} criterios sin responder` : `${unanswered.length} criteria not answered`);
      return;
    }

    setSubmitting(true);
    try {
      await axiosInstance.post('/inspections/evaluations', {
        ...formData,
        criteria: criteriaResults
      });
      toast.success(isEs ? 'Evaluacion enviada exitosamente' : 'Evaluation submitted successfully');
      setShowFormDialog(false);
      loadEvaluations();
    } catch (err) {
      console.error('Failed to submit:', err);
      toast.error(isEs ? 'Error al enviar evaluacion' : 'Failed to submit evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(isEs ? 'Eliminar esta evaluacion?' : 'Delete this evaluation?')) return;
    try {
      await axiosInstance.delete(`/inspections/evaluations/${id}`);
      toast.success(isEs ? 'Evaluacion eliminada' : 'Evaluation deleted');
      loadEvaluations();
    } catch (err) {
      toast.error(isEs ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  const viewEvaluation = (ev) => {
    setViewingEvaluation(ev);
    setShowViewDialog(true);
  };

  const downloadEvaluationPdf = async (ev) => {
    try {
      toast.info(isEs ? 'Generando PDF...' : 'Generating PDF...');
      const response = await axiosInstance.get(`/inspections/evaluations/${ev.id}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (ev.employeeName || 'evaluation').replace(/\s+/g, '_');
      const date = ev.evaluationDate || 'undated';
      link.download = `operator_evaluation_${safeName}_${date}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(isEs ? 'PDF descargado' : 'PDF downloaded');
    } catch (err) {
      console.error('Failed to download PDF:', err);
      toast.error(err.response?.data?.detail || (isEs ? 'Error al descargar PDF' : 'Failed to download PDF'));
    }
  };

  const updateCriterion = (id, field, value) => {
    setCriteriaResults(prev =>
      prev.map(c => c.id === id ? { ...c, [field]: value } : c)
    );
  };

  const getResultBadge = (result) => {
    switch (result) {
      case 'authorized':
        return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />{isEs ? 'Autorizado' : 'Authorized'}</Badge>;
      case 'not_authorized':
        return <Badge className="bg-red-100 text-red-800 border-red-200"><XCircle className="w-3 h-3 mr-1" />{isEs ? 'No Autorizado' : 'Not Authorized'}</Badge>;
      case 'requires_training':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200"><AlertTriangle className="w-3 h-3 mr-1" />{isEs ? 'Requiere Capacitacion' : 'Requires Training'}</Badge>;
      default:
        return null;
    }
  };

  const getCriteriaLabel = (criterion) => {
    return isEs ? criterion.labelEs : criterion.label;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {isEs ? 'Evaluaciones de Operador' : 'Operator Performance Evaluations'}
          </h2>
          <p className="text-sm text-gray-600">
            {isEs ? 'Evaluaciones de Montacargas y Gradall - OSHA 29 CFR 1910.178(I)' : 'Forklift & Gradall Equipment - OSHA 29 CFR 1910.178(I)'}
          </p>
        </div>
        <Button onClick={openNewForm} className="bg-orange-600 hover:bg-orange-700" data-testid="new-evaluation-btn">
          <Plus className="w-4 h-4 mr-2" />
          {isEs ? 'Nueva Evaluacion' : 'New Evaluation'}
        </Button>
      </div>

      {/* Evaluations List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto"></div>
        </div>
      ) : evaluations.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <ClipboardCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              {isEs ? 'Sin evaluaciones aun' : 'No evaluations yet'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {isEs ? 'Crea tu primera evaluacion de operador' : 'Create your first operator performance evaluation'}
            </p>
            <Button onClick={openNewForm} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" />
              {isEs ? 'Crear Evaluacion' : 'Create Evaluation'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {evaluations.map((ev) => (
            <Card key={ev.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h4 className="font-semibold text-gray-900">{ev.employeeName}</h4>
                      {getResultBadge(ev.evaluationResult)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{ev.equipmentType}</span>
                      <span>{new Date(ev.evaluationDate).toLocaleDateString()}</span>
                      <span>{isEs ? 'Evaluador' : 'Evaluator'}: {ev.trainerEvaluator}</span>
                      {ev.summary && (
                        <span className="text-xs">
                          <span className="text-green-600">{ev.summary.pass}P</span> / <span className="text-red-600">{ev.summary.fail}F</span> / <span className="text-gray-400">{ev.summary.na}N/A</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => viewEvaluation(ev)} data-testid={`view-eval-${ev.id}`}>
                      <Eye className="w-4 h-4 mr-1" />
                      {isEs ? 'Ver' : 'View'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadEvaluationPdf(ev)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      data-testid={`download-eval-${ev.id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      {isEs ? 'PDF' : 'PDF'}
                    </Button>
                    {(user.role === 'company_admin' || user.role === 'super_admin' || user.role === 'supervisor') && (
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(ev.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* === NEW EVALUATION FORM DIALOG === */}
      <Dialog open={showFormDialog} onOpenChange={setShowFormDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ClipboardCheck className="w-6 h-6 text-orange-600" />
              {isEs ? 'Evaluacion de Desempeno del Operador' : 'Operator Performance Evaluation'}
            </DialogTitle>
            <DialogDescription>
              {isEs ? 'Montacargas y Equipo Gradall' : 'Forklift & Gradall Equipment'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-2">
            {/* Employee Info Section */}
            <div className="bg-slate-50 rounded-lg p-4 border">
              <h3 className="font-semibold text-gray-800 mb-3">
                {isEs ? 'Informacion del Empleado' : 'Employee Information'}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{isEs ? 'Nombre del Empleado' : 'Employee Name'} *</Label>
                  <Input
                    value={formData.employeeName}
                    onChange={(e) => setFormData({ ...formData, employeeName: e.target.value })}
                    placeholder={isEs ? 'Nombre completo' : 'Full name'}
                    data-testid="eval-employee-name"
                  />
                </div>
                <div>
                  <Label>{isEs ? 'Empresa' : 'Company'}</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    data-testid="eval-company"
                  />
                </div>
                <div>
                  <Label>{isEs ? 'Tipo de Equipo' : 'Equipment Type'}</Label>
                  <Input
                    value={formData.equipmentType}
                    onChange={(e) => setFormData({ ...formData, equipmentType: e.target.value })}
                    data-testid="eval-equipment-type"
                  />
                </div>
                <div>
                  <Label>{isEs ? 'Fecha de Evaluacion' : 'Evaluation Date'} *</Label>
                  <Input
                    type="date"
                    value={formData.evaluationDate}
                    onChange={(e) => setFormData({ ...formData, evaluationDate: e.target.value })}
                    data-testid="eval-date"
                  />
                </div>
                <div className="col-span-2">
                  <Label>{isEs ? 'Entrenador / Evaluador' : 'Trainer / Evaluator'} *</Label>
                  <Input
                    value={formData.trainerEvaluator}
                    onChange={(e) => setFormData({ ...formData, trainerEvaluator: e.target.value })}
                    data-testid="eval-trainer"
                  />
                </div>
              </div>
            </div>

            {/* Evaluation Criteria */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-orange-50 px-4 py-3 border-b">
                <h3 className="font-semibold text-orange-900">
                  {isEs ? 'Criterios de Evaluacion' : 'Evaluation Criteria'}
                </h3>
              </div>

              {/* Table Header */}
              <div className="grid grid-cols-[1fr_60px_60px_60px_1fr] gap-0 bg-gray-100 text-xs font-semibold text-gray-600 px-4 py-2 border-b">
                <div>{isEs ? 'Criterio' : 'Criteria'}</div>
                <div className="text-center">{isEs ? 'Aprueba' : 'Pass'}</div>
                <div className="text-center">{isEs ? 'Falla' : 'Fail'}</div>
                <div className="text-center">N/A</div>
                <div>{isEs ? 'Comentarios' : 'Comments'}</div>
              </div>

              {/* Criteria Rows */}
              {EVALUATION_CRITERIA.map((criterion, index) => {
                const currentResult = criteriaResults.find(c => c.id === criterion.id);
                return (
                  <div key={criterion.id} className={`grid grid-cols-[1fr_60px_60px_60px_1fr] gap-0 items-center px-4 py-3 border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <div className="text-sm text-gray-800 pr-2">
                      <span className="text-gray-400 mr-2">{index + 1}.</span>
                      {getCriteriaLabel(criterion)}
                    </div>
                    <div className="flex justify-center">
                      <input
                        type="radio"
                        name={`criterion-${criterion.id}`}
                        checked={currentResult?.result === 'pass'}
                        onChange={() => updateCriterion(criterion.id, 'result', 'pass')}
                        className="w-4 h-4 text-green-600 accent-green-600 cursor-pointer"
                        data-testid={`eval-${criterion.id}-pass`}
                      />
                    </div>
                    <div className="flex justify-center">
                      <input
                        type="radio"
                        name={`criterion-${criterion.id}`}
                        checked={currentResult?.result === 'fail'}
                        onChange={() => updateCriterion(criterion.id, 'result', 'fail')}
                        className="w-4 h-4 text-red-600 accent-red-600 cursor-pointer"
                        data-testid={`eval-${criterion.id}-fail`}
                      />
                    </div>
                    <div className="flex justify-center">
                      <input
                        type="radio"
                        name={`criterion-${criterion.id}`}
                        checked={currentResult?.result === 'na'}
                        onChange={() => updateCriterion(criterion.id, 'result', 'na')}
                        className="w-4 h-4 text-gray-400 accent-gray-500 cursor-pointer"
                        data-testid={`eval-${criterion.id}-na`}
                      />
                    </div>
                    <div>
                      <Input
                        value={currentResult?.comments || ''}
                        onChange={(e) => updateCriterion(criterion.id, 'comments', e.target.value)}
                        placeholder={isEs ? 'Comentarios...' : 'Comments...'}
                        className="text-sm h-8"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Evaluation Result */}
            <div className="bg-slate-50 rounded-lg p-4 border">
              <h3 className="font-semibold text-gray-800 mb-3">
                {isEs ? 'Resultado de la Evaluacion' : 'Evaluation Result'} *
              </h3>
              <RadioGroup
                value={formData.evaluationResult}
                onValueChange={(v) => setFormData({ ...formData, evaluationResult: v })}
                className="space-y-3"
              >
                <div className="flex items-center space-x-3 p-3 rounded-lg border bg-white hover:bg-green-50 transition cursor-pointer">
                  <RadioGroupItem value="authorized" id="authorized" data-testid="eval-result-authorized" />
                  <Label htmlFor="authorized" className="flex items-center gap-2 cursor-pointer flex-1">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium">{isEs ? 'Autorizado' : 'Authorized'}</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border bg-white hover:bg-red-50 transition cursor-pointer">
                  <RadioGroupItem value="not_authorized" id="not_authorized" data-testid="eval-result-not-authorized" />
                  <Label htmlFor="not_authorized" className="flex items-center gap-2 cursor-pointer flex-1">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="font-medium">{isEs ? 'No Autorizado' : 'Not Authorized'}</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border bg-white hover:bg-yellow-50 transition cursor-pointer">
                  <RadioGroupItem value="requires_training" id="requires_training" data-testid="eval-result-training" />
                  <Label htmlFor="requires_training" className="flex items-center gap-2 cursor-pointer flex-1">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    <span className="font-medium">{isEs ? 'Requiere Capacitacion Adicional' : 'Requires Additional Training'}</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Evaluator Signature */}
            <div className="bg-slate-50 rounded-lg p-4 border">
              <h3 className="font-semibold text-gray-800 mb-3">
                {isEs ? 'Firma del Evaluador' : 'Evaluator Signature'} *
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{isEs ? 'Firma (nombre completo)' : 'Signature (full name)'}</Label>
                  <Input
                    value={formData.evaluatorSignature}
                    onChange={(e) => setFormData({ ...formData, evaluatorSignature: e.target.value })}
                    placeholder={isEs ? 'Escribe tu nombre completo' : 'Type your full name'}
                    className="font-serif italic"
                    data-testid="eval-signature"
                  />
                </div>
                <div>
                  <Label>{isEs ? 'Fecha' : 'Date'}</Label>
                  <Input
                    type="date"
                    value={formData.signatureDate}
                    onChange={(e) => setFormData({ ...formData, signatureDate: e.target.value })}
                    data-testid="eval-signature-date"
                  />
                </div>
              </div>
            </div>

            {/* OSHA Reference */}
            <p className="text-xs text-gray-500 text-center italic">
              {isEs
                ? 'Evaluacion realizada de acuerdo con OSHA 29 CFR 1910.178(I) y 29 CFR 1926 Subpart O.'
                : 'Evaluation conducted in accordance with OSHA 29 CFR 1910.178(I) and 29 CFR 1926 Subpart O.'}
            </p>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowFormDialog(false)}>
              {isEs ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-orange-600 hover:bg-orange-700" data-testid="eval-submit">
              {submitting ? (isEs ? 'Enviando...' : 'Submitting...') : (isEs ? 'Enviar Evaluacion' : 'Submit Evaluation')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === VIEW EVALUATION DIALOG === */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              {isEs ? 'Evaluacion de Desempeno del Operador' : 'Operator Performance Evaluation'}
            </DialogTitle>
          </DialogHeader>

          {viewingEvaluation && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4 border">
                <div><span className="text-sm text-gray-500">{isEs ? 'Empleado' : 'Employee'}:</span> <strong>{viewingEvaluation.employeeName}</strong></div>
                <div><span className="text-sm text-gray-500">{isEs ? 'Empresa' : 'Company'}:</span> <strong>{viewingEvaluation.company || '-'}</strong></div>
                <div><span className="text-sm text-gray-500">{isEs ? 'Equipo' : 'Equipment'}:</span> <strong>{viewingEvaluation.equipmentType}</strong></div>
                <div><span className="text-sm text-gray-500">{isEs ? 'Fecha' : 'Date'}:</span> <strong>{new Date(viewingEvaluation.evaluationDate).toLocaleDateString()}</strong></div>
                <div><span className="text-sm text-gray-500">{isEs ? 'Evaluador' : 'Evaluator'}:</span> <strong>{viewingEvaluation.trainerEvaluator}</strong></div>
                <div>{getResultBadge(viewingEvaluation.evaluationResult)}</div>
              </div>

              {/* Criteria Results */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-orange-50 px-4 py-2 border-b">
                  <h4 className="font-semibold text-orange-900 text-sm">{isEs ? 'Criterios de Evaluacion' : 'Evaluation Criteria'}</h4>
                </div>
                {viewingEvaluation.criteria?.map((c, i) => {
                  const criterionDef = EVALUATION_CRITERIA.find(def => def.id === c.id);
                  return (
                    <div key={c.id} className={`flex items-center gap-3 px-4 py-2 text-sm border-b ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <span className="text-gray-400 w-6">{i + 1}.</span>
                      <span className="flex-1 text-gray-800">{criterionDef ? getCriteriaLabel(criterionDef) : c.id}</span>
                      <span className={`font-semibold w-12 text-center ${c.result === 'pass' ? 'text-green-600' : c.result === 'fail' ? 'text-red-600' : 'text-gray-400'}`}>
                        {c.result === 'pass' ? (isEs ? 'SI' : 'PASS') : c.result === 'fail' ? (isEs ? 'NO' : 'FAIL') : 'N/A'}
                      </span>
                      {c.comments && <span className="text-gray-500 text-xs italic max-w-[200px] truncate">{c.comments}</span>}
                    </div>
                  );
                })}
              </div>

              {/* Signature */}
              <div className="bg-gray-50 rounded-lg p-4 border flex items-center justify-between">
                <div>
                  <span className="text-sm text-gray-500">{isEs ? 'Firma del Evaluador' : 'Evaluator Signature'}:</span>
                  <p className="font-serif italic text-lg">{viewingEvaluation.evaluatorSignature}</p>
                </div>
                <div className="text-right">
                  <span className="text-sm text-gray-500">{isEs ? 'Fecha' : 'Date'}:</span>
                  <p>{new Date(viewingEvaluation.signatureDate).toLocaleDateString()}</p>
                </div>
              </div>

              <p className="text-xs text-gray-400 text-center italic">
                OSHA 29 CFR 1910.178(I) / 29 CFR 1926 Subpart O
              </p>
            </div>
          )}
          <DialogFooter>
            {viewingEvaluation && (
              <Button
                onClick={() => downloadEvaluationPdf(viewingEvaluation)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="eval-view-download-pdf"
              >
                <Download className="w-4 h-4 mr-2" />
                {isEs ? 'Descargar PDF' : 'Download PDF'}
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              {isEs ? 'Cerrar' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperatorEvaluation;
